/*
 * Copyright (c) 2023 SARL SIGMA POLYNESIA
 */

import { useQuery } from '@tanstack/react-query'
import rest from '../../../../client/js/lib/rest'
import { stringify } from 'qs'
import { useMemo } from 'react'
import CounterWidget from './widgets/CounterWidget'
import { DateTime } from 'luxon'
import { FormattedMessage, useIntl } from 'react-intl'
import RevenuesPerMonthChart from './charts/RevenuesPerMonthChart'
import RevenuesPerYearChart from './charts/RevenuesPerYearChart'
import LeasesPerMonthChart from './charts/LeasesPerMonthChart'
import EstatesPerMonthChart from './charts/EstatesPerMonthChart'
import LeasesPerYearChart from './charts/LeasesPerYearChart'
import EstatesPerYearChart from './charts/EstatesPerYearChart'
import { Card, Container, Group, SimpleGrid, Stack, Title } from '@mantine/core'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { useSettingsContext } from '../SettingsProvider'
import Page from '../../../../client/js/components/Page'

function DashboardPage () {
  const intl = useIntl()
  const { settings } = useSettingsContext()

  const params = useMemo(() => {
    const currentMonth = DateTime.now()
    return {
      fromDate: currentMonth.startOf('month').toISO(),
      toDate: currentMonth.endOf('month').toISO()
    }
  }, [])

  const query = useQuery({
    queryKey: [params],
    queryFn: () => rest.get(`/api/dashboard?${stringify(params)}`).then((resp) => resp.json()),
    enabled: params.fromDate != null && params.toDate != null,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000
  })

  const stats = useMemo(() => query.data?.data || {}, [query.data?.data])

  return (
    <Page>
      <LoadingHelper
        query={query}
        render={() => (
          <Container size="xl">
            <Stack>
              <Card shadow="sm">
                <Group>
                  <CounterWidget
                    label={intl.formatMessage({
                      id: 'revenues_excluding_tax_this_month',
                      defaultMessage: 'Revenus HT ce mois'
                    })}
                    value={stats?.totalRevenuesThisMonth}
                    options={{ style: 'currency', currency: settings.CURRENCY }}
                  />
                  <CounterWidget
                    label={intl.formatMessage({
                      id: 'revenues_excluding_tax_last_12_months_excluding_tax',
                      defaultMessage: 'Revenus HT des 12 derniers mois'
                    })}
                    value={stats?.totalRevenuesLast12Months}
                    options={{ style: 'currency', currency: settings.CURRENCY }}
                  />
                </Group>
              </Card>
              <SimpleGrid cols={{ lg: 2 }}>
                <Card shadow="sm">
                  <Title order={2} size="h3" ta="center">
                    <FormattedMessage
                      id="revenues_per_month_excluding_tax"
                      defaultMessage="Revenus HT par mois"
                    />
                  </Title>
                  <RevenuesPerMonthChart
                    mih={400}
                    data={stats.revenuesPerMonth}
                  />
                </Card>
                <Card shadow="sm">
                  <Title order={2} size="h3" ta="center">
                    <FormattedMessage
                      id="revenues_per_year_excluding_tax"
                      defaultMessage="Revenus HT par année"
                    />
                  </Title>
                  <RevenuesPerYearChart
                    mih={400}
                    data={stats.revenuesPerYear}
                  />
                </Card>

                <Card shadow="sm">
                  <Title order={2} size="h3" ta="center">
                    <FormattedMessage
                      id="leases_per_month"
                      defaultMessage="Dossiers de location par mois"
                    />
                  </Title>
                  <LeasesPerMonthChart
                    mih={400}
                    data={stats.leasesStartingPerMonth}
                  />
                </Card>
                <Card shadow="sm">
                  <Title order={2} size="h3" ta="center">
                    <FormattedMessage
                      id="leases_per_year"
                      defaultMessage="Dossiers de location par année"
                    />
                  </Title>
                  <LeasesPerYearChart
                    mih={400}
                    data={stats.leasesStartingPerYear}
                  />
                </Card>

                <Card shadow="sm">
                  <Title order={2} size="h3" ta="center">
                    <FormattedMessage
                      id="estates_created_per_month"
                      defaultMessage="Biens créés par mois"
                    />
                  </Title>
                  <EstatesPerMonthChart
                    mih={400}
                    data={stats.estatesCreatedPerMonth}
                  />
                </Card>
                <Card shadow="sm">
                  <Title order={2} size="h3" ta="center">
                    <FormattedMessage
                      id="estates_created_per_year"
                      defaultMessage="Biens créés par année"
                    />
                  </Title>
                  <EstatesPerYearChart
                    mih={400}
                    data={stats.estatesCreatedPerYear}
                  />
                </Card>
              </SimpleGrid>
            </Stack>
          </Container>
        )}
      />
    </Page>
  )
}

export default DashboardPage
